import type {
  MembershipBillingOption,
  MembershipCoverageRule,
  MembershipPlan,
} from '@/components/organisms/pricebook/support/membership-plan-shared';
import {
  billingIntervalForOption,
  legacyBillingOption,
} from '@/components/organisms/pricebook/support/membership-plan-shared';
import { extraUnitPriceOf } from './extra-unit-price';

export interface MembershipSaleEquipment {
  id: string;
  category: string;
  name?: string | null;
  serialNumber?: string | null;
}

/** Return the exact selected set, or nothing when a paged/stale query omitted one of its ids. */
export function resolveMembershipSaleEquipmentSelection(
  equipmentIds: string[],
  equipment: MembershipSaleEquipment[],
): MembershipSaleEquipment[] | undefined {
  const equipmentById = new Map(equipment.map((unit) => [unit.id, unit]));
  const resolved = equipmentIds.map((equipmentId) => equipmentById.get(equipmentId));
  return resolved.every((unit): unit is MembershipSaleEquipment => Boolean(unit))
    ? resolved
    : undefined;
}

function money(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Resolve the option exactly as Operations does when saving a membership. */
export function membershipBillingOptionForSale(
  plan: MembershipPlan,
  requestedOptionId?: string | null,
): MembershipBillingOption | undefined {
  const options = plan.billingOptions?.length ? plan.billingOptions : [legacyBillingOption(plan)];
  // No chosen option means the plan's offered price — never a free option's.
  const offered = options.filter((option) => !option.isComplimentary);
  return requestedOptionId
    ? options.find((option) => option.id === requestedOptionId)
    : (offered.find((option) => option.isDefault) ?? offered[0]);
}

export function membershipBillingPeriodMonths(option: MembershipBillingOption): number {
  if (option.frequency === 'UPFRONT') return Math.max(1, Math.trunc(option.termMonths || 1));
  const interval = billingIntervalForOption(option);
  if (!interval) return 1;
  return interval.billingIntervalUnit === 'YEARS'
    ? Math.max(1, Math.trunc(interval.billingIntervalValue)) * 12
    : Math.max(1, Math.trunc(interval.billingIntervalValue));
}

/** "per year", "per quarter", "up front for 24 months" — how one charge of this option recurs. */
export function membershipBillingPeriodLabel(option: MembershipBillingOption): string {
  const months = membershipBillingPeriodMonths(option);
  if (option.frequency === 'UPFRONT') return `up front for ${months} months`;
  if (months === 1) return 'per month';
  if (months === 3) return 'per quarter';
  if (months === 12) return 'per year';
  return `every ${months} months`;
}

/** Mirrors the authoritative sold-home calculation in Operations. */
export function priceMembershipOptionForEquipment(input: {
  option: MembershipBillingOption;
  coverageRules?: MembershipCoverageRule[];
  equipment?: MembershipSaleEquipment[];
}): { priceAmount: number; equipmentIds: string[]; extraPeriodAmount: number } {
  const activeEquipment = input.equipment ?? [];
  const byCategory = new Map<string, MembershipSaleEquipment[]>();
  for (const unit of activeEquipment) {
    const key = unit.category.trim().toLowerCase();
    const rows = byCategory.get(key) ?? [];
    if (!rows.some((row) => row.id === unit.id)) rows.push(unit);
    byCategory.set(key, rows);
  }

  // Monthly add-ons: each row's monthly charge in cents, summed, times the
  // months in the period. Yearly add-ons: prorated from the yearly figure.
  let monthlyExtra = 0;
  let yearlyExtra = 0;
  const equipmentIds: string[] = [];
  for (const rule of input.coverageRules ?? []) {
    const units = byCategory.get(rule.equipmentCategory.trim().toLowerCase()) ?? [];
    equipmentIds.push(...units.map((unit) => unit.id));
    const extraUnits = Math.max(0, units.length - Math.max(0, Math.trunc(rule.includedQuantity)));
    const price = extraUnitPriceOf(rule);
    if (price.extraUnitPricePer === 'YEAR') {
      yearlyExtra += extraUnits * price.extraUnitPrice;
    } else {
      monthlyExtra += money(extraUnits * price.extraUnitPrice);
    }
  }

  const periodMonths = membershipBillingPeriodMonths(input.option);
  const periodExtra = money(monthlyExtra) * periodMonths + (yearlyExtra * periodMonths) / 12;
  return {
    priceAmount: money(Math.max(0, input.option.basePrice) + periodExtra),
    equipmentIds: Array.from(new Set(equipmentIds)),
    extraPeriodAmount: money(periodExtra),
  };
}
